import { ComponentChildren, Fragment } from 'preact';
import { useState } from 'preact/hooks';
import { rpx } from 'client/lib/rpx-client';
import { BtnPrimary, BtnSecondary, Button } from '@components/buttons';
import { IcoCheckCircle } from '@components/icons';
import { TrackerCloseAs, scoreboard, scoreboardDetails } from 'shared/tracker-rollup';
import {
  fmtMoney,
  fmtTrackerDate,
  moneyInputValue,
  parseMoneyCents,
  TrackerBet,
} from 'shared/tracker-people';
import { leadsSetUp, leadsText, SET_UP_FIRST_MESSAGE, spendBreakdown } from 'shared/tracker-leads';
import { betData, betsScoreboardLine, breakdownBets } from 'shared/tracker-summary';
import { FieldError } from './panel';
import {
  CARD_CLASS,
  HELP_CLASS,
  INPUT_CLASS,
  LABEL_CLASS,
  LINK_CLASS,
  Person,
  readError,
  TrackerData,
} from './helpers';
import { BetsFields, partialSave, SavedBets, useBetsDraft } from './bets';
import { AllSpendList, ByChannelTable, LeadsSaved, LeadsSetUpForm, TrendTable } from './leads';

type Participant = TrackerData['participant'];

/** The participant's bets for the set-up form, on a course with bets on. */
export type SetupBets = {
  list: TrackerBet[];
  /** Every person, not only a filtered view's: Remove checks who is under a bet. */
  people: Person[];
  onChange: (saved: SavedBets) => void;
};

type CardProps = {
  /** Always the whole tracker: one target, so the bet switch leaves this card alone. */
  data: TrackerData;
  courseId: UUID;
  onParticipant: (participant: Participant) => void;
  bets?: SetupBets;
  /** Cost per lead: a set-up save, and the Add spend panel. */
  leads?: { onSaved: (saved: LeadsSaved) => void; openSpend: () => void };
};

export function ScoreboardCard(props: CardProps) {
  if (props.data.config.scoreboard.kind === 'cpl') {
    return <CplCard {...props} />;
  }
  return <RevenueCard {...props} />;
}

function RevenueCard({ data, courseId, onParticipant, bets }: CardProps) {
  const [changing, setChanging] = useState(false);
  const { participant, today } = data;
  if (!participant.closeAs || changing) {
    return (
      <ScoreboardSetup
        courseId={courseId}
        participant={participant}
        isChange={!!participant.closeAs}
        bets={bets}
        onSaved={(p) => {
          onParticipant(p);
          setChanging(false);
        }}
        onPartial={(p) => {
          onParticipant(p);
          setChanging(true);
        }}
        onCancel={participant.closeAs ? () => setChanging(false) : undefined}
      />
    );
  }
  const board = scoreboard(data.config, data.touches, participant);
  if (board.kind !== 'revenue') {
    return;
  }
  const word = participant.closeAs === 'booked' ? 'booked' : 'collected';
  const target = participant.targetCents ?? 0;
  const reached = target > 0 && board.progressCents >= target;
  const percent = target > 0 ? Math.min(100, Math.round((board.progressCents / target) * 100)) : 0;
  const betsLine = betsScoreboardLine(data);
  return (
    <section class={CARD_CLASS} aria-label="Scoreboard">
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        {reached ? (
          <h2 class="flex items-center gap-2 text-base font-semibold text-green-700 dark:text-green-400">
            <IcoCheckCircle class="size-5" />
            Target reached · {fmtMoney(board.progressCents)} {word} of {fmtMoney(target)}
          </h2>
        ) : (
          <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
            Scoreboard · {fmtMoney(board.progressCents)} {word} of {fmtMoney(target)}
          </h2>
        )}
        {!reached && (
          <span class="text-sm text-gray-600 dark:text-gray-400">
            {fmtMoney(target - board.progressCents)} to go
          </span>
        )}
      </div>
      <div
        class="h-2 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label="Progress to target"
      >
        <div
          class={`h-full ${reached ? 'bg-green-500' : 'bg-indigo-500'}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {betsLine && <p class="text-sm text-gray-700 dark:text-gray-300 tabular-nums">{betsLine}</p>}
      <p class="hidden sm:block text-sm text-gray-600 dark:text-gray-400">
        A close counts when the money is {word}. Your choice
        {participant.closeAsSetOn ? `, set ${fmtTrackerDate(participant.closeAsSetOn, today)}` : ''}
        .{' '}
        <Button class={LINK_CLASS} onClick={() => setChanging(true)}>
          Change
        </Button>
      </p>
      <ScoreboardDetails data={data}>
        <p class="sm:hidden text-sm text-gray-600 dark:text-gray-400">
          A close counts when the money is {word}. Your choice
          {participant.closeAsSetOn
            ? `, set ${fmtTrackerDate(participant.closeAsSetOn, today)}`
            : ''}
          .{' '}
          <Button class={LINK_CLASS} onClick={() => setChanging(true)}>
            Change
          </Button>
        </p>
      </ScoreboardDetails>
    </section>
  );
}

/** A row per bet: the figures that add up to the total above. */
function ByBetTable({ data }: { data: TrackerData }) {
  const bets = breakdownBets(data);
  if (!bets.length) {
    return;
  }
  const columns = [
    'People contacted',
    'Replies',
    data.config.rollupLabels.held,
    'Wins',
    'Booked',
    'Collected',
  ];
  return (
    <div class="mt-3 flex flex-col gap-1">
      <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">By bet</h3>
      <div class="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-md">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-600 dark:text-gray-400">
            <tr>
              <th class="px-3 py-2 text-left font-medium">Bet</th>
              {columns.map((c) => (
                <th key={c} class="px-3 py-2 text-right font-medium">
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            {bets.map((bet) => {
              const ofBet = betData(data, bet.id);
              const d = scoreboardDetails(data.config, ofBet.touches, data.participant);
              const values = [
                String(d.peopleContacted),
                String(d.replies),
                String(d.held),
                String(d.wins),
                fmtMoney(d.bookedCents),
                fmtMoney(d.collectedCents),
              ];
              return (
                <tr key={bet.id}>
                  <th
                    scope="row"
                    class="px-3 py-2 text-left font-medium text-gray-900 dark:text-gray-100"
                  >
                    {bet.name}
                  </th>
                  {values.map((v, i) => (
                    <td
                      key={i}
                      class="px-3 py-2 text-right tabular-nums text-gray-900 dark:text-gray-100"
                    >
                      {v}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScoreboardDetails({
  data,
  children,
}: {
  data: TrackerData;
  children?: ComponentChildren;
}) {
  const d = scoreboardDetails(data.config, data.touches, data.participant);
  const rows: Array<[string, string]> = [
    ['People contacted', String(d.peopleContacted)],
    ['Replies', `${d.replies} · ${d.interested} interested`],
    ['Reply rate', d.replyRate === undefined ? '–' : `${d.replyRate.toFixed(1)}%`],
    [data.config.rollupLabels.held, String(d.held)],
    ['Decisions landed', String(d.decisions)],
    ['Wins', String(d.wins)],
    ['Booked', fmtMoney(d.bookedCents)],
    ['Collected', fmtMoney(d.collectedCents)],
    ['Gap to target', d.gapCents === undefined ? '–' : fmtMoney(d.gapCents)],
  ];
  return (
    <details class="text-sm">
      <summary class="cursor-pointer text-indigo-600 dark:text-indigo-400 w-fit">Details</summary>
      <div class="mt-2">{children}</div>
      <dl class="mt-2 grid grid-cols-[1fr_auto] gap-x-6 gap-y-1 max-w-sm">
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <dt class="text-gray-600 dark:text-gray-400">{label}</dt>
            <dd class="text-gray-900 dark:text-gray-100 text-right tabular-nums">{value}</dd>
          </Fragment>
        ))}
      </dl>
      <ByBetTable data={data} />
    </details>
  );
}

/**
 * The revenue target and close choice, and on a course with bets on the
 * participant's bets too: "Set up your tracker", or "Scoreboard and bets"
 * once set up. The scoreboard's Change
 * opens the same form, with Remove on each bet.
 */
export function ScoreboardSetup({
  courseId,
  participant,
  isChange,
  bets,
  onSaved,
  onPartial,
  onCancel,
}: {
  courseId: UUID;
  participant: Participant;
  isChange: boolean;
  bets?: SetupBets;
  onSaved: (participant: Participant) => void;
  /**
   * The scoreboard saved but the bets didn't: the page takes the saved
   * participant while the form stays open on the bets error. Defaults to
   * onSaved.
   */
  onPartial?: (participant: Participant) => void;
  onCancel?: () => void;
}) {
  const [target, setTarget] = useState(moneyInputValue(participant.targetCents));
  const [closeAs, setCloseAs] = useState<TrackerCloseAs | undefined>(participant.closeAs);
  const [error, setError] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const draft = useBetsDraft(bets?.list ?? [], bets?.people ?? []);
  const title = !bets
    ? 'Set up your scoreboard'
    : isChange
      ? 'Scoreboard and bets'
      : 'Set up your tracker';

  async function save(e: Event) {
    e.preventDefault();
    const targetCents = parseMoneyCents(target);
    if (targetCents === undefined) {
      setError('Add your revenue target. Use numbers only, like 10000.');
      return;
    }
    if (!closeAs) {
      setError('Pick when a close counts.');
      return;
    }
    const betsProblem = bets ? draft.problem() : undefined;
    if (betsProblem) {
      setError(undefined);
      draft.setError(betsProblem);
      return;
    }
    setSaving(true);
    setError(undefined);
    try {
      const saved = await rpx.tracker.setScoreboard({ courseId, closeAs, targetCents });
      if (bets) {
        try {
          bets.onChange(await draft.save(courseId));
        } catch (err) {
          const savedBets = partialSave(err);
          if (savedBets) {
            bets.onChange(savedBets);
          }
          (onPartial ?? onSaved)(saved);
          return;
        }
      }
      onSaved(saved);
    } catch (err) {
      setError(readError(err).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form class={CARD_CLASS} onSubmit={save} aria-label={title}>
      <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
      <label class="flex flex-col gap-1">
        <span class={LABEL_CLASS}>Revenue target</span>
        <span class="flex items-center gap-2">
          <span class="text-gray-500 dark:text-gray-400">$</span>
          <input
            class={`${INPUT_CLASS} max-w-44`}
            inputMode="decimal"
            value={target}
            onInput={(e) => setTarget(e.currentTarget.value)}
          />
        </span>
      </label>
      <fieldset class="flex flex-col gap-2" role="radiogroup">
        <legend class={`${LABEL_CLASS} mb-1`}>A close counts when the money is</legend>
        <label class="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="close-as"
            class="mt-1"
            checked={closeAs === 'booked'}
            onChange={() => setCloseAs('booked')}
          />
          <span>
            <span class="font-medium">Booked</span>
            <span class="block text-gray-600 dark:text-gray-400">They say yes</span>
          </span>
        </label>
        <label class="flex items-start gap-2 text-sm">
          <input
            type="radio"
            name="close-as"
            class="mt-1"
            checked={closeAs === 'collected'}
            onChange={() => setCloseAs('collected')}
          />
          <span>
            <span class="font-medium">Collected</span>
            <span class="block text-gray-600 dark:text-gray-400">
              Their first payment arrives, as your Calibration Card defines it
            </span>
          </span>
        </label>
        {isChange && closeAs !== participant.closeAs && (
          <p class="text-sm text-amber-800 dark:text-amber-300">
            Your past weekly closes are recounted the new way.
          </p>
        )}
      </fieldset>
      <p class={HELP_CLASS}>
        Your scoreboard and your weekly closes both use it. You can change it later.
      </p>
      {bets && <BetsFields draft={draft} />}
      <FieldError id="scoreboard-error" message={error} />
      <div class="flex gap-2">
        <BtnPrimary type="submit" isLoading={saving}>
          {bets ? 'Save' : 'Save scoreboard'}
        </BtnPrimary>
        {onCancel && (
          <BtnSecondary type="button" onClick={onCancel}>
            Cancel
          </BtnSecondary>
        )}
      </div>
    </form>
  );
}

function CplCard({ data, courseId, leads }: CardProps) {
  const [changing, setChanging] = useState(false);
  const { participant } = data;
  const board = scoreboard(data.config, data.touches, participant, data.spend);
  if (board.kind !== 'cpl' || !leads) {
    return;
  }
  const hasHours = data.spend.some((e) => e.minutes);
  if (changing) {
    return (
      <LeadsSetUpForm
        courseId={courseId}
        participant={participant}
        hasHours={hasHours}
        isChange={leadsSetUp(participant)}
        onSaved={(saved) => {
          leads.onSaved(saved);
          setChanging(false);
        }}
        onCancel={() => setChanging(false)}
      />
    );
  }
  if (!leadsSetUp(participant) || board.targetCents === undefined) {
    return (
      <section class={CARD_CLASS} aria-label="Scoreboard">
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">Scoreboard</h2>
        <p class="text-sm text-gray-700 dark:text-gray-300">{SET_UP_FIRST_MESSAGE}</p>
        <BtnSecondary class="self-start" onClick={() => setChanging(true)}>
          Set up your tracker
        </BtnSecondary>
      </section>
    );
  }
  const target = board.targetCents;
  const over =
    board.cplCents !== undefined && board.cplCents > target ? board.cplCents - target : 0;
  return (
    <section class={CARD_CLASS} aria-label="Scoreboard">
      <div class="flex flex-wrap items-baseline justify-between gap-2">
        <h2 class="text-base font-semibold text-gray-900 dark:text-gray-100">
          {board.cplCents !== undefined
            ? `Scoreboard · ${fmtMoney(board.cplCents)} per lead`
            : board.leads > 0
              ? `Scoreboard · ${leadsText(board.leads)}`
              : 'Scoreboard'}
        </h2>
        <span class="text-sm text-gray-600 dark:text-gray-400">
          Your target: {fmtMoney(target)} or less
        </span>
      </div>
      {over > 0 && (
        <p class="text-sm font-medium text-amber-700 dark:text-amber-400">
          {fmtMoney(over)} over your target
        </p>
      )}
      <p class="text-sm text-gray-700 dark:text-gray-300 tabular-nums">
        {leadsText(board.leads)} · {spendBreakdown(board, participant.hourlyRateCents)}
        {' ·\u00a0'}
        <Button class={`${LINK_CLASS} whitespace-nowrap`} onClick={leads.openSpend}>
          Add spend
        </Button>
      </p>
      {board.cplCents === undefined && board.leads > 0 && (
        <p class={HELP_CLASS}>Add your spend to see cost per lead.</p>
      )}
      <p class="text-sm text-gray-600 dark:text-gray-400">
        A lead reaches “{participant.destination}”. Hours at{' '}
        {fmtMoney(participant.hourlyRateCents ?? 0)}.{' '}
        <Button class={LINK_CLASS} onClick={() => setChanging(true)}>
          Change
        </Button>
      </p>
      <details class="text-sm">
        <summary class="cursor-pointer text-indigo-600 dark:text-indigo-400 w-fit">Details</summary>
        <div class="mt-2 flex flex-col gap-4">
          <TrendTable data={data} />
          <ByChannelTable data={data} />
          <AllSpendList data={data} />
        </div>
      </details>
    </section>
  );
}
